import { Box, type BoxRef } from "../../../../../../mads";
import { type TabConfig, TabMenu } from "../../../../../../mads/TabMenu/TabMenu";
import { useIsReducedMotionEnabled } from "../../../../../../mads/util/useIsReducedMotionEnabled";
import {
  GHOSTWRITER_PRIMARY_ACTIONS,
  shouldShowSecondaryApply,
  useGhostwriterForViewJob,
} from "../../../../../../shared/ghostwriter/context/useGhostwriterForViewJob";
import {
  logGhostwriterEntrypointImpression,
  logGhostwriterEntrypointTapFirstTime,
  logGhostwriterEntrypointTapReturning,
  logGhostwriterThirdPartyApplyStart,
} from "../../../../../../shared/ghostwriter/logging/lib";
import { GhostwriterActionBar } from "../../../../../../shared/ghostwriter/viewjob/GhostwriterActionBar";
import { GhostwriterApplyButton } from "../../../../../../shared/ghostwriter/viewjob/GhostwriterApplyButton";
import { usePublishMainContentHeight } from "../../../../../../shared/hooks/useMainContentLayout";
import { useI18n } from "../../../../../../shared/I18n";
import { dispatch as dispatchBroadcast } from "../../../../../../shared/native-modules/tare/jstBroadcast";
import { JSTLandingScreen } from "../../../../../../shared/native-modules/tare/JSTLandingScreen";
import { JSTModalScreen } from "../../../../../../shared/native-modules/tare/JSTModalScreen";
import { logScreenView } from "../../../../../../shared/native-modules/tare/logScreenView";
import navigateTo from "../../../../../../shared/native-modules/tare/navigateTo";
import { openModal } from "../../../../../../shared/native-modules/tare/openModal";
import eventEmitter from "../../../../../../shared/util/eventEmitter";
import { useViewjobProctorAllocation } from "../../../../../proctor/__generated__/ViewjobProctorContext";
import {
  G4jExpiredVjRecommendedJobsTst,
  JsjRnCompanyTabTst,
  MegOffsiteJobalertRnvjTst,
  MobVjJobSummaryTst,
} from "../../../../../proctor/__generated__/ViewjobProctorSpecifications";
import { DwellTrackedSection } from "../../../../context/DwellTrackedSection";
import {
  useProctorGroups,
  useViewJobDataContext,
  useViewjobOneGraphModel,
} from "../../../../context/ViewJobDataContext";
import { useRnViewJobDisplay } from "../../../../context/ViewJobDisplayContext";
import { ScreenDisplay, useIsDesktop } from "../../../../context/ViewJobScreenDisplay";
import { useAccessibilityFocus } from "../../../../hooks/useAccessibilityFocus";
import { useDesktopEmbeddedHeaderCollapse } from "../../../../hooks/useDesktopEmbeddedHeaderCollapse";
import { useEmployerOutreachInfo } from "../../../../hooks/useEmployerOutreachInfo";
import { OutreachStatus } from "../../../../hooks/useEmployerOutreachUIConfig";
import { useFullScreenVJActive } from "../../../../hooks/useFullScreenVJActive";
import { markJobAsDisliked } from "../../../../lib/markJobAsDisliked";
import { useCloseVJ } from "../../../../lib/useCloseVJ";
import { isJobFeedbackPromptEnabled } from "../../../../util/proctorHelper";
import { useIsSticky } from "../../../../util/useIsSticky";
import { isJobFromHomePage, isJobFromSerp } from "../../../../utils/fromParamUtils";
import { InterviewPrepCTA } from "../../../mosaic/InterviewPrepCTA/InterviewPrepCTA";
import { AskCareerScoutCTA } from "../../AskCareerScoutStarters/AskCareerScoutCTA";
import {
  CAREER_SCOUT_ALWAYS_AWARE_BUCKETS,
  useCareerScoutAlwaysAwareBucket,
} from "../../AskCareerScoutStarters/proctor";
import { ViewJobDivider } from "../../Divider";
import { WidthTracker } from "../../HtmlContent/WidthTracker";
import { ViewJobJobAlertSignup } from "../../JobAlertSignup";
import { PostApplyAd } from "../../PostApplyAd";
import { ReportContentButton } from "../../ReportContent/ReportContent/ReportContentButton";
import { AdditionalLinks } from "../AdditionalLinks";
import { ApplyLimitBanner, useIsApplyLimitBannerVisible } from "../ApplyLimitBanner";
import { ApplySection } from "../ApplySection";
import { BasicJobInfoSection } from "../BasicJobInfoSection";
import { OGWrappedBrandedHeader } from "../BrandedHeader";
import { LazyCompanyConquestingJobCards } from "../CompanyConquesting/LazyCompanyConquestingJobCards";
import { useShouldLoadCompanyConquesting } from "../CompanyConquesting/useShouldLoadCompanyConquesting";
import {
  ConfirmInterestButton,
  ErrorAlert,
  useIsConfirmInterestButtonVisible,
} from "../ConfirmInterestButton";
import { WithOptionalCTAEnticerBanner } from "../CTAEnticerBanner/WithOptionalCTAEnticerBanner";
import { DeclineButton, useIsDeclineButtonVisible } from "../DeclineButton";
import { DesktopEmbeddedCompactHeader } from "../DesktopEmbeddedCompactHeader/DesktopEmbeddedCompactHeader";
import { DesktopShareDropdown } from "../DesktopShareDropdown";
import { FAQFootprint } from "../FAQFootprint/FAQFootprint";
import { OGWrappedIndeedApplyButton } from "../IndeedApplyButton";
import { useInterviewNow } from "../InterviewNow/useInterviewNow";
import { JobContent } from "../JobContent/JobContent";
import { JobFeedbackPrompt } from "../JobFeedbackPrompt";
import { JobHeaderActions } from "../JobHeaderActions/JobHeaderActions";
import { JobInfoHeader } from "../JobInfoHeader";
import { LogoBannerFlair } from "../LogoBannerFlair";
import { OGWrappedNexusJobDisclaimer } from "../NexusJobDisclaimer";
import { RelatedLinks } from "../RelatedLinks/RelatedLinks";
import { SavedApplicationBanner } from "../SavedApplicationBanner";
import { BottomSaveJobButton } from "../SaveJobButton/BottomSaveJobButton";
import { SaveJobButton } from "../SaveJobButton/SaveJobButton";
import { StandaloneDesktopSearchBox } from "../StandaloneDesktopSearchBox/StandaloneDesktopSearchBox";
import { StickyButton } from "../StickyButtons/StickyButtons";
import { ViewJobButtonLink } from "../ViewJobButtonLink";
import { ViewJobDislikeButton } from "../ViewJobDislikeButton/ViewJobDislikeButton";
import { WithSafeAreaInsetsTopMargin } from "../WithSafeAreaInsetsMarginTop/WithSafeAreaInsetsTopMargin";

import { colors, shadows, spacing } from "@indeed/ifl-tokens/react-native";
import React, { type ReactNode, Suspense, useCallback, useEffect, useRef, useState } from "react";
import { Animated, Platform, StyleSheet, type ViewStyle } from "react-native";

const EmployerOutreach = React.lazy(() =>
  import(
    /* webpackChunkName: "employer-outreach" */
    "../EmployerOutreach/EmployerOutreach"
  ).then((module) => ({ default: module.EmployerOutreach })),
);

const CompanyContent = React.lazy(() =>
  import(
    /* webpackChunkName: "company-content" */
    "../CompanyContent/CompanyContent"
  ).then((module) => ({ default: module.CompanyContent })),
);

const StickyApplyWithAskCTA = ({ children }: { children: React.ReactNode }) => (
  <Box sx={styles.buttonRowWithAsk}>
    <Box sx={styles.applyButtonInRow}>{children}</Box>
    <AskCareerScoutCTA compact />
  </Box>
);

export enum ViewJobVariant {
  Default = "default",
  FullScreen = "fullscreen",
}

export type ViewJobMainContentProps = {
  jobKey: string;
  loggedIn: boolean;
  disclaimer?: string;
  /**
   * Whether this job uses Indeed Apply (as opposed to third-party apply).
   * Used for conditional rendering of the apply button section.
   * The actual apply URL is loaded asynchronously via context.
   */
  isIndeedApplyJob?: boolean;
  reload: () => void;
  variant?: ViewJobVariant;
};

type WebTransitionStyle = ViewStyle & {
  gridTemplateRows?: string;
  overflowAnchor?: "auto" | "none";
  transitionDuration?: string;
  transitionProperty?: string;
  transitionTimingFunction?: string;
  visibility?: "hidden" | "visible";
};

export function getPrimaryApplyButtonSize(
  variant: ViewJobVariant,
  isDesktop: boolean,
  isCompactDesktopHeader: boolean,
): "md" | undefined {
  return variant === ViewJobVariant.FullScreen || isDesktop || isCompactDesktopHeader
    ? "md"
    : undefined;
}

export function ViewJobMainContent({
  jobKey,
  loggedIn,
  disclaimer,
  isIndeedApplyJob,
  reload,
  variant = ViewJobVariant.Default,
}: ViewJobMainContentProps) {
  const focus = useAccessibilityFocus();
  const topRef = useRef<BoxRef>(null);

  useEffect(() => {
    if (topRef.current) {
      return focus(topRef.current);
    }
  }, [focus]);

  usePublishMainContentHeight(topRef, true);

  const i18n = useI18n();
  const viewjobOneGraphModel = useViewjobOneGraphModel();
  const { companyImagesModel } = viewjobOneGraphModel?.employer || {};
  const proctorGroups = useProctorGroups();
  const interviewNowModel = useInterviewNow();
  const hideBenefitsSection = useViewjobProctorAllocation(MobVjJobSummaryTst).value >= 2;
  const hideJobDetailsSection = [3, 4, 5].includes(
    useViewjobProctorAllocation(MobVjJobSummaryTst).value,
  );
  // Hide location section on mobile (show only on desktop)
  const isDesktop = useIsDesktop();
  const hasDesktopBrandedHeader =
    isDesktop && variant === ViewJobVariant.Default && !!companyImagesModel?.showBannerTop;
  const viewJobDisplay = useRnViewJobDisplay();
  const isDesktopEmbedded = viewJobDisplay === "DESKTOP_EMBEDDED";
  const isDesktopStandalone = viewJobDisplay === "DESKTOP_STANDALONE";
  const isReducedMotionEnabled = useIsReducedMotionEnabled();
  const { isCompact, sentinelRef } = useDesktopEmbeddedHeaderCollapse();
  const showCompactDesktopHeader = variant === ViewJobVariant.Default && isCompact;
  // TODO: Clean it up once we fixed all the issues for company tab
  const isCompanyTabTestActive = useViewjobProctorAllocation(JsjRnCompanyTabTst).isActive;
  const isJobAlertSignupEnabled = useViewjobProctorAllocation(MegOffsiteJobalertRnvjTst).isActive;
  const isExpiredVjRecommendedJobsActive =
    useViewjobProctorAllocation(G4jExpiredVjRecommendedJobsTst).value > 0;
  const moveRecommendedJobsBelowExpiredHeader =
    isExpiredVjRecommendedJobsActive && !!viewjobOneGraphModel?.job.isExpired;
  const shouldLoadCompanyConquesting = useShouldLoadCompanyConquesting();
  const isAskCTAVariant =
    useCareerScoutAlwaysAwareBucket() === CAREER_SCOUT_ALWAYS_AWARE_BUCKETS.ASK;
  const hasDeclineButton = useIsDeclineButtonVisible();
  const hasConfirmInterestButton = useIsConfirmInterestButtonVisible(jobKey);
  const isSticky = useIsSticky();
  const { from, initialData } = useViewJobDataContext();
  const shouldShowDesktopDislike = loggedIn && (initialData?.dislikeFrom2paneEnabled ?? false);
  const hasAdditionalLinksModel = !!viewjobOneGraphModel?.additionalLinksViewModel;
  const [hasConfirmInterestError, setHasConfirmInterestError] = useState(false);
  const [hasDeclineError, setHasDeclineError] = useState(false);
  const [isDesktopJobDisliked, setIsDesktopJobDisliked] = useState(false);
  const resetErrorRef = useRef<(() => void) | null>(null);
  const closeVJ = useCloseVJ();

  useEffect(() => {
    setIsDesktopJobDisliked(false);
  }, [jobKey]);
  // TODO: Track schedule apply button visibility and log impression
  const {
    buttonAction,
    applicationId,
    // phase_d clones phase_a's ViewJob experience, so use the combined
    // Prefill-experience flag rather than isPhaseA here. See RN-3367.
    usesPrefillExperience: usesGhostwriterPrefillExperience,
    isPhaseB: isGhostwriterPhaseB,
    isPhaseC: isGhostwriterPhaseC,
    isPhaseE: isGhostwriterPhaseE,
    isAutoApplyLoading,
    beginAutoApplyLoading,
    startAutoApply,
    stopAutoApply,
    refreshApplication,
  } = useGhostwriterForViewJob(jobKey);
  const hasGhostwriterPrimary = GHOSTWRITER_PRIMARY_ACTIONS.includes(buttonAction);
  const jobIsExpired = !!viewjobOneGraphModel?.job.isExpired;
  const showGhostwriterApplySection = hasGhostwriterPrimary && !jobIsExpired;
  const showSecondaryApply = shouldShowSecondaryApply(buttonAction);

  // Defined here so it will persist across GhostwriterApplyButton remounts,
  // preventing the animation from replaying
  const ghostwriterAnim = useRef(new Animated.Value(0)).current;

  const hasLoggedGhostwriterEntrypointImpression = useRef(false);
  useEffect(() => {
    if (hasGhostwriterPrimary && !hasLoggedGhostwriterEntrypointImpression.current) {
      hasLoggedGhostwriterEntrypointImpression.current = true;
      logGhostwriterEntrypointImpression(jobKey);
    }
  }, [hasGhostwriterPrimary, jobKey]);

  const emitAutoApplyStarted = useCallback(async () => {
    eventEmitter.emit("startAutoApply", { jobKey });
    try {
      await dispatchBroadcast({ eventType: "ApplyStart", applyType: "autoApply" });
    } catch (error) {
      console.error("Failed to dispatch startAutoApply broadcast", error);
    }
  }, [jobKey]);

  // The prefilling modal signals one of three intents on dismissal,
  // and ViewJob is responsible for any follow-up navigation (iOS
  // forbids push navigation from a presented view controller that
  // has not been dismissed — see ScheduleApplyPrefillingResultT in
  // the TARE RFC):
  //   - OPEN_REVIEW: polling reached review-ready and the modal
  //     auto-dismissed. ViewJob navigates to the application
  //     review screen. ViewJob stays open underneath.
  //   - RETURN_TO_JOBS: user tapped "Return to jobs". ViewJob
  //     closes itself and navigates to Home so the user lands on
  //     the Home tab regardless of which tab opened ViewJob.
  //   - DISMISSED: X close. ViewJob stays put (the CTA will now
  //     read "Check application status").
  //
  // applicationId is read via a ref because the `.then` closure is
  // captured at the time `openModal` is invoked — typically right
  // after `startAutoApply` resolves, before the Apollo cache has
  // notified `useGhostwriterForViewJob`. The prefilling modal's
  // polling is the source of truth for `applicationId` and writes
  // it into the GhostwriterStore, which ViewJob is subscribed to;
  // by the time the modal signals OPEN_REVIEW the latest id is in
  // the ref even if it was null when openModal was first called.
  const applicationIdRef = useRef(applicationId);
  useEffect(() => {
    applicationIdRef.current = applicationId;
  }, [applicationId]);

  // After kicking off the auto-apply backend job, present the "Prefilling your
  // application" progress modal on top of ViewJob rather than closing it.
  const openPrefillingModalIfPrefillPhase = useCallback(() => {
    // phase_a/b/c/e use the synchronous Prefill flow (open the modal here); only
    // the active bucket stays headless and just closes ViewJob.
    if (
      !usesGhostwriterPrefillExperience &&
      !isGhostwriterPhaseB &&
      !isGhostwriterPhaseC &&
      !isGhostwriterPhaseE
    ) {
      closeVJ();
      return;
    }
    void openModal(JSTModalScreen.scheduleApplyPrefilling(jobKey), "viewjob")
      .then((result) => {
        switch (result?.result) {
          case "OPEN_REVIEW": {
            const latestApplicationId = applicationIdRef.current;
            if (!latestApplicationId) {
              console.error(
                "Prefilling modal signalled OPEN_REVIEW but no applicationId is available",
              );
              return;
            }
            void navigateTo(
              JSTLandingScreen.scheduleApplyApplicationReview(latestApplicationId),
              "viewjob",
            ).catch(console.error);
            return;
          }
          case "RETURN_TO_JOBS": {
            closeVJ();
            void navigateTo(JSTLandingScreen.home(), "viewjob").catch(console.error);
            return;
          }
          case "DISMISSED":
            void refreshApplication();
            return;
          default:
            return;
        }
      })
      .catch(console.error);
  }, [
    usesGhostwriterPrefillExperience,
    isGhostwriterPhaseB,
    isGhostwriterPhaseC,
    isGhostwriterPhaseE,
    jobKey,
    closeVJ,
    refreshApplication,
  ]);

  const handleTryAutoApply = useCallback(() => {
    logGhostwriterEntrypointTapFirstTime(jobKey);
    beginAutoApplyLoading();
    void openModal(JSTModalScreen.scheduleApplyOnboarding(jobKey), "viewjob")
      .then(async (result) => {
        if (result?.result === "COMPLETED") {
          await emitAutoApplyStarted();
          openPrefillingModalIfPrefillPhase();
        } else {
          stopAutoApply();
        }
      })
      .catch(stopAutoApply);
  }, [
    jobKey,
    beginAutoApplyLoading,
    emitAutoApplyStarted,
    openPrefillingModalIfPrefillPhase,
    stopAutoApply,
  ]);

  const handleAutoApply = useCallback(() => {
    logGhostwriterEntrypointTapReturning(jobKey);
    startAutoApply()
      .then(async () => {
        await emitAutoApplyStarted();
        openPrefillingModalIfPrefillPhase();
      })
      // TODO: surface submission failure to the user (e.g. toast / error banner)
      .catch(console.error);
  }, [jobKey, startAutoApply, emitAutoApplyStarted, openPrefillingModalIfPrefillPhase]);

  const handleReviewApplication = useCallback(() => {
    if (!applicationId) return;
    void navigateTo(
      JSTLandingScreen.scheduleApplyApplicationReview(applicationId),
      "viewjob",
    ).catch(console.error);
  }, [applicationId]);

  const handleCheckStatusInMyJobs = useCallback(() => {
    closeVJ();
    void navigateTo(JSTLandingScreen.myJobsSubTab("ghostwriterAutoApply"), "viewjob").catch(
      console.error,
    );
  }, [closeVJ]);

  const handleThirdPartyApplyPress = useCallback(() => {
    if (!jobKey) return;
    logGhostwriterThirdPartyApplyStart(jobKey, buttonAction);
  }, [jobKey, buttonAction]);

  // Check if interest has been confirmed - if so, hide all CTA buttons in enticer
  const outreachResult = useEmployerOutreachInfo(jobKey);
  const isInterestConfirmed =
    outreachResult?.outreachInfo?.outreachStatus === OutreachStatus.INTERESTED;
  const isEmployerOutreachJob = !!outreachResult;

  const handleConfirmInterestError = useCallback(() => {
    setHasConfirmInterestError(true);
  }, []);

  const handleConfirmInterestErrorDismiss = useCallback(() => {
    setHasConfirmInterestError(false);
    resetErrorRef.current?.();
  }, []);

  const handleDeclineError = useCallback(() => {
    setHasDeclineError(true);
  }, []);

  const handleDeclineErrorDismiss = useCallback(() => {
    setHasDeclineError(false);
  }, []);

  // Check if company tab should be shown
  const fccKey = viewjobOneGraphModel?.employer.key;
  const mobtk = viewjobOneGraphModel?.mobvjtk;
  const hasCompanyTab =
    viewjobOneGraphModel?.features.isCompanyTabActive &&
    !!fccKey &&
    !!mobtk &&
    isCompanyTabTestActive;
  const shouldShowApplyLimitBanner = useIsApplyLimitBannerVisible();

  // Tab state management
  const [selectedTabId, setSelectedTabId] = useState<string>("job");

  // Tab configuration
  const tabs: TabConfig[] = [
    {
      id: "job",
      label: "Job",
    },
    {
      id: "company",
      label: "Company",
    },
  ];

  const handleTabPress = (tabId: string) => {
    setSelectedTabId(tabId);
  };

  // Switching between floating and fixed states during page scrolling will reset the internal state of the component.
  // To only log the impression once, use a ref here to track if the prompt has been logged.
  const hasLoggedJobFeedbackPromptImpression = useRef(false);
  const hasLoggedCTABannerImpression = useRef(false);
  const hasLoggedIndeedApplyPrimaryCTAImpression = useRef(false);
  const hasLoggedIndeedApplySecondaryCTAImpression = useRef(false);

  // ensures the seen log for IA button is called only once. This is needed because the button is remounted whenever stickiness changed.
  const [hasLoggedIndeedApplySeen, setHasLoggedIndeedApplySeen] = useState(false);

  const logJobFeedbackPromptShown = useCallback(() => {
    if (hasLoggedJobFeedbackPromptImpression.current) {
      return;
    }
    hasLoggedJobFeedbackPromptImpression.current = true;
    logScreenView("rn-view-job-feedback-prompt").catch(console.error);
  }, []);

  const handlePressDislike = () => {
    if (isJobFromHomePage(from)) {
      eventEmitter.emit("dislikeJob", {
        jobKey: jobKey,
        vjtk: viewjobOneGraphModel?.mobvjtk ?? "",
      });
    } else if (isJobFromSerp(from)) {
      void markJobAsDisliked(jobKey);
    }
    void closeVJ();
  };

  let primaryApplyButton: ReactNode | null;
  if (shouldShowApplyLimitBanner) {
    primaryApplyButton = null;
  } else if (isIndeedApplyJob) {
    primaryApplyButton = (
      <OGWrappedIndeedApplyButton
        hasLoggedImpression={hasLoggedIndeedApplyPrimaryCTAImpression}
        hasLoggedSeen={hasLoggedIndeedApplySeen}
        setHasLoggedSeen={setHasLoggedIndeedApplySeen}
        buttonSize={getPrimaryApplyButtonSize(variant, isDesktop, showCompactDesktopHeader)}
        disabled={isDesktopJobDisliked}
      />
    );
  } else {
    primaryApplyButton = (
      <ViewJobButtonLink
        hasLoggedSeen={hasLoggedIndeedApplySeen}
        setHasLoggedSeen={setHasLoggedIndeedApplySeen}
        buttonSize={getPrimaryApplyButtonSize(variant, isDesktop, showCompactDesktopHeader)}
        disabled={isDesktopJobDisliked}
      />
    );
  }

  if (primaryApplyButton) {
    primaryApplyButton = (
      <DwellTrackedSection section="cta">{primaryApplyButton}</DwellTrackedSection>
    );
  }

  return (
    <Box
      ref={topRef}
      testID="viewjob-main-content"
      sx={[
        styles.container,
        isDesktopStandalone && styles.desktopStandaloneContainer,
        variant === ViewJobVariant.FullScreen && styles.fullScreenVJContainer,
      ]}
    >
      <StandaloneDesktopSearchBox />
      <PostApplyAd />
      {variant === ViewJobVariant.FullScreen ? (
        <LogoBannerFlair companyImagesModel={companyImagesModel} />
      ) : (
        companyImagesModel?.showBannerTop && <OGWrappedBrandedHeader />
      )}
      {isDesktopEmbedded && variant === ViewJobVariant.Default && (
        <Box
          aria-hidden
          pointerEvents="none"
          ref={sentinelRef}
          sx={styles.desktopHeaderSentinel}
          testID="desktop-header-sticky-sentinel"
        />
      )}
      <WidthTracker width={2 * CONTENT_MARGIN}>
        <Box
          testID="desktop-job-header"
          sx={[
            styles.jobHeader,
            styles.contentSpacing,
            variant === ViewJobVariant.FullScreen && styles.fullScreenVJContentSpacing,
            isDesktop && styles.desktopJobHeader,
            hasDesktopBrandedHeader && styles.desktopBrandedJobHeader,
            isDesktopStandalone && styles.desktopStandaloneContentSpacing,
            isDesktopEmbedded && styles.desktopHeaderTransition,
            isDesktopEmbedded && isReducedMotionEnabled && styles.desktopHeaderReducedMotion,
            showCompactDesktopHeader && styles.compactDesktopJobHeader,
          ]}
        >
          {variant === ViewJobVariant.FullScreen ? (
            <BasicJobInfoSection
              isExpired={viewjobOneGraphModel?.job.isExpired}
              employerActivity={viewjobOneGraphModel?.job.employerActivity}
              tags={viewjobOneGraphModel?.job.tags}
            />
          ) : (
            <>
              {isDesktopEmbedded ? (
                <>
                  <Box
                    aria-hidden={showCompactDesktopHeader}
                    pointerEvents={showCompactDesktopHeader ? "none" : "auto"}
                    sx={[
                      styles.desktopHeaderState,
                      showCompactDesktopHeader && styles.desktopHeaderStateHidden,
                      isReducedMotionEnabled && styles.desktopHeaderStateReducedMotion,
                    ]}
                  >
                    <Box sx={styles.desktopHeaderStateContent}>
                      <JobInfoHeader />
                    </Box>
                  </Box>
                  <Box
                    aria-hidden={!showCompactDesktopHeader}
                    pointerEvents={showCompactDesktopHeader ? "auto" : "none"}
                    sx={[
                      styles.desktopHeaderState,
                      !showCompactDesktopHeader && styles.desktopHeaderStateHidden,
                      isReducedMotionEnabled && styles.desktopHeaderStateReducedMotion,
                    ]}
                  >
                    <Box sx={styles.desktopHeaderStateContent}>
                      <DesktopEmbeddedCompactHeader />
                    </Box>
                  </Box>
                </>
              ) : (
                <JobInfoHeader />
              )}
              <ScreenDisplay.DesktopOnly>
                <JobHeaderActions
                  compact={showCompactDesktopHeader}
                  primaryApply={primaryApplyButton}
                  dislikeJob={
                    shouldShowDesktopDislike ? (
                      <ViewJobDislikeButton
                        jobKey={jobKey}
                        jobTitle={viewjobOneGraphModel?.job.jobTitle}
                        trackingKey={viewjobOneGraphModel?.mobvjtk}
                        initialIsSaved={viewjobOneGraphModel?.job.savedJob?.state === "SAVED"}
                        onDislikeStateChange={setIsDesktopJobDisliked}
                      />
                    ) : undefined
                  }
                  saveJob={
                    <SaveJobButton
                      jobKey={jobKey}
                      isLoggedIn={loggedIn}
                      reload={() => {}}
                      variant="utility"
                    />
                  }
                  shareJob={<DesktopShareDropdown jobKey={jobKey} />}
                />
              </ScreenDisplay.DesktopOnly>
              <ViewJobDivider />
            </>
          )}
          {moveRecommendedJobsBelowExpiredHeader && shouldLoadCompanyConquesting && (
            <DwellTrackedSection section="otherJobRecommendations">
              <LazyCompanyConquestingJobCards jobKey={jobKey} loggedIn={loggedIn} />
            </DwellTrackedSection>
          )}
          {isEmployerOutreachJob && (
            <Suspense fallback={null}>
              <EmployerOutreach jobKey={jobKey} variant={variant} />
            </Suspense>
          )}
        </Box>
        <SavedApplicationBanner />
        {hasCompanyTab ? (
          <>
            <TabMenu
              tabs={tabs}
              full={false}
              size={"sm"}
              selectedTabId={selectedTabId}
              onTabPress={handleTabPress}
              sx={styles.tabMenuContainer}
              a11yLabels={{ description: i18n.gettext("Job details navigation") }}
            />

            <Box
              testID="viewjob-job-content"
              sx={[
                styles.contentSpacingWithTabMenu,
                isDesktopStandalone && styles.desktopStandaloneContentSpacing,
                variant === ViewJobVariant.FullScreen && styles.fullScreenVJContentSpacing,
              ]}
            >
              {selectedTabId === "job" && (
                <JobContent
                  jobKey={jobKey}
                  loggedIn={loggedIn}
                  reload={reload}
                  interviewNowModel={interviewNowModel}
                  hideBenefitsSection={hideBenefitsSection}
                  hideJobDetailsSection={hideJobDetailsSection}
                  hideJobLocationSection={true}
                  hideCompanyConquesting={
                    moveRecommendedJobsBelowExpiredHeader || !shouldLoadCompanyConquesting
                  }
                />
              )}
              {selectedTabId === "company" && (
                <Suspense fallback={null}>
                  <CompanyContent fccKey={fccKey} mobtk={mobtk} />
                </Suspense>
              )}
            </Box>
          </>
        ) : (
          <Box
            testID="viewjob-job-content"
            sx={[
              styles.contentSpacing,
              isDesktopStandalone && styles.desktopStandaloneContentSpacing,
              variant === ViewJobVariant.FullScreen && styles.fullScreenVJContentSpacing,
            ]}
          >
            <JobContent
              jobKey={jobKey}
              loggedIn={loggedIn}
              reload={reload}
              interviewNowModel={interviewNowModel}
              hideBenefitsSection={hideBenefitsSection}
              hideJobDetailsSection={hideJobDetailsSection}
              hideJobLocationSection={true}
              hideCompanyConquesting={
                moveRecommendedJobsBelowExpiredHeader || !shouldLoadCompanyConquesting
              }
            />
          </Box>
        )}

        <ScreenDisplay.MobileOnly>
          <OGWrappedNexusJobDisclaimer />
        </ScreenDisplay.MobileOnly>

        <ScreenDisplay.MobileOnly>
          {shouldShowApplyLimitBanner && (
            <Box sx={{ marginBottom: 48 }}>
              <ApplyLimitBanner />
            </Box>
          )}

          {!shouldShowApplyLimitBanner && (
            <>
              {isIndeedApplyJob ? (
                <StickyButton
                  stickyAreaBackgroundColor={isEmployerOutreachJob ? colors.background : undefined}
                >
                  {isJobFeedbackPromptEnabled(proctorGroups) && (
                    <Box sx={styles.savePromptContainer}>
                      <JobFeedbackPrompt
                        jobKey={jobKey}
                        isLoggedIn={loggedIn}
                        reload={reload}
                        onShown={logJobFeedbackPromptShown}
                      />
                    </Box>
                  )}
                  <DwellTrackedSection section="cta">
                    <WithOptionalCTAEnticerBanner
                      hasLoggedImpression={hasLoggedCTABannerImpression}
                      jobKey={jobKey}
                      variant={variant}
                    >
                      {!isInterestConfirmed && (
                        <>
                          {hasConfirmInterestError && (
                            <ErrorAlert onDismiss={handleConfirmInterestErrorDismiss} />
                          )}
                          {hasDeclineError && <ErrorAlert onDismiss={handleDeclineErrorDismiss} />}
                          {hasConfirmInterestButton ? (
                            hasDeclineButton && isSticky ? (
                              <Box sx={styles.buttonRowWithDecline}>
                                <DeclineButton jobKey={jobKey} onError={handleDeclineError} />
                                <Box sx={styles.confirmInterestButtonSticky}>
                                  <ConfirmInterestButton
                                    jobKey={jobKey}
                                    onError={handleConfirmInterestError}
                                    resetErrorRef={resetErrorRef}
                                  />
                                </Box>
                              </Box>
                            ) : (
                              <Box sx={styles.confirmInterestButtonNonSticky}>
                                <ConfirmInterestButton
                                  jobKey={jobKey}
                                  onError={handleConfirmInterestError}
                                  resetErrorRef={resetErrorRef}
                                />
                              </Box>
                            )
                          ) : hasDeclineButton && isSticky ? (
                            <Box sx={styles.buttonRowWithDecline}>
                              <DeclineButton jobKey={jobKey} onError={handleDeclineError} />
                              <Box sx={[styles.applyButtonSticky, { marginHorizontal: 0 }]}>
                                <OGWrappedIndeedApplyButton
                                  hasLoggedImpression={hasLoggedIndeedApplyPrimaryCTAImpression}
                                  hasLoggedSeen={hasLoggedIndeedApplySeen}
                                  setHasLoggedSeen={setHasLoggedIndeedApplySeen}
                                />
                              </Box>
                            </Box>
                          ) : isAskCTAVariant && isSticky ? (
                            <StickyApplyWithAskCTA>
                              <OGWrappedIndeedApplyButton
                                hasLoggedImpression={hasLoggedIndeedApplyPrimaryCTAImpression}
                                hasLoggedSeen={hasLoggedIndeedApplySeen}
                                setHasLoggedSeen={setHasLoggedIndeedApplySeen}
                              />
                            </StickyApplyWithAskCTA>
                          ) : (
                            <Box
                              sx={[
                                styles.applyButtonNonSticky,
                                variant === ViewJobVariant.FullScreen && {
                                  paddingBottom: 0,
                                },
                              ]}
                            >
                              {variant === ViewJobVariant.FullScreen ? (
                                <ApplySection
                                  onPressDislike={handlePressDislike}
                                  applyButton={
                                    <OGWrappedIndeedApplyButton
                                      hasLoggedImpression={hasLoggedIndeedApplyPrimaryCTAImpression}
                                      hasLoggedSeen={hasLoggedIndeedApplySeen}
                                      setHasLoggedSeen={setHasLoggedIndeedApplySeen}
                                      buttonSize="md"
                                    />
                                  }
                                  secondaryButton={
                                    isAskCTAVariant ? <AskCareerScoutCTA /> : undefined
                                  }
                                  compactSecondaryButton={
                                    isAskCTAVariant ? <AskCareerScoutCTA compact /> : undefined
                                  }
                                />
                              ) : (
                                <>
                                  <OGWrappedIndeedApplyButton
                                    hasLoggedImpression={hasLoggedIndeedApplyPrimaryCTAImpression}
                                    hasLoggedSeen={hasLoggedIndeedApplySeen}
                                    setHasLoggedSeen={setHasLoggedIndeedApplySeen}
                                  />
                                  {isAskCTAVariant && <AskCareerScoutCTA />}
                                </>
                              )}
                            </Box>
                          )}
                        </>
                      )}
                    </WithOptionalCTAEnticerBanner>
                  </DwellTrackedSection>
                </StickyButton>
              ) : (
                <StickyButton
                  stickyAreaBackgroundColor={
                    showGhostwriterApplySection || disclaimer || isEmployerOutreachJob
                      ? colors.background
                      : undefined
                  }
                >
                  {isJobFeedbackPromptEnabled(proctorGroups) && (
                    <Box sx={styles.savePromptContainer}>
                      <JobFeedbackPrompt
                        jobKey={jobKey}
                        isLoggedIn={loggedIn}
                        reload={reload}
                        onShown={logJobFeedbackPromptShown}
                      />
                    </Box>
                  )}
                  <DwellTrackedSection section="cta">
                    {showGhostwriterApplySection ? (
                      <GhostwriterActionBar
                        showTopDivider={true}
                        isPhaseA={usesGhostwriterPrefillExperience}
                      >
                        <Box sx={styles.applyButtonNonSticky}>
                          <GhostwriterApplyButton
                            buttonAction={buttonAction}
                            onTryAutoApply={handleTryAutoApply}
                            onAutoApply={handleAutoApply}
                            onReviewApplication={handleReviewApplication}
                            useProgressLoadingButton={false}
                            onCheckStatusInMyJobs={handleCheckStatusInMyJobs}
                            isPhaseA={usesGhostwriterPrefillExperience}
                            animValue={ghostwriterAnim}
                            isLoading={isAutoApplyLoading}
                          />
                        </Box>
                        {showSecondaryApply && (
                          <Box sx={styles.applyButtonNonSticky}>
                            <ViewJobButtonLink
                              hasLoggedSeen={hasLoggedIndeedApplySeen}
                              setHasLoggedSeen={setHasLoggedIndeedApplySeen}
                              buttonVariant="secondary"
                              shouldHaveShadow={false}
                              onApplyPress={jobKey ? handleThirdPartyApplyPress : undefined}
                            />
                          </Box>
                        )}
                      </GhostwriterActionBar>
                    ) : (
                      <WithOptionalCTAEnticerBanner
                        hasLoggedImpression={hasLoggedCTABannerImpression}
                        jobKey={jobKey}
                        variant={variant}
                      >
                        {hasDeclineError && <ErrorAlert onDismiss={handleDeclineErrorDismiss} />}
                        {hasDeclineButton && isSticky ? (
                          <Box sx={styles.buttonRowWithDecline}>
                            <DeclineButton jobKey={jobKey} onError={handleDeclineError} />
                            <Box sx={styles.applyButtonSticky}>
                              <ViewJobButtonLink
                                hasLoggedSeen={hasLoggedIndeedApplySeen}
                                setHasLoggedSeen={setHasLoggedIndeedApplySeen}
                              />
                            </Box>
                          </Box>
                        ) : isAskCTAVariant && isSticky ? (
                          <StickyApplyWithAskCTA>
                            <ViewJobButtonLink
                              hasLoggedSeen={hasLoggedIndeedApplySeen}
                              setHasLoggedSeen={setHasLoggedIndeedApplySeen}
                            />
                          </StickyApplyWithAskCTA>
                        ) : (
                          <Box
                            sx={[
                              styles.applyButtonNonSticky,
                              variant === ViewJobVariant.FullScreen && {
                                paddingBottom: 0,
                              },
                            ]}
                          >
                            {variant === ViewJobVariant.FullScreen ? (
                              <ApplySection
                                onPressDislike={handlePressDislike}
                                applyButton={
                                  <ViewJobButtonLink
                                    hasLoggedSeen={hasLoggedIndeedApplySeen}
                                    setHasLoggedSeen={setHasLoggedIndeedApplySeen}
                                    buttonSize="md"
                                  />
                                }
                                secondaryButton={
                                  isAskCTAVariant ? <AskCareerScoutCTA /> : undefined
                                }
                                compactSecondaryButton={
                                  isAskCTAVariant ? <AskCareerScoutCTA compact /> : undefined
                                }
                              />
                            ) : (
                              <>
                                <ViewJobButtonLink
                                  hasLoggedSeen={hasLoggedIndeedApplySeen}
                                  setHasLoggedSeen={setHasLoggedIndeedApplySeen}
                                />
                                {isAskCTAVariant && (
                                  <Box sx={styles.askCTASpacing}>
                                    <AskCareerScoutCTA />
                                  </Box>
                                )}
                              </>
                            )}
                          </Box>
                        )}
                      </WithOptionalCTAEnticerBanner>
                    )}
                  </DwellTrackedSection>
                </StickyButton>
              )}
            </>
          )}

          <WithSafeAreaInsetsTopMargin>
            {hasDeclineButton && !isSticky && (
              <>
                {hasDeclineError && <ErrorAlert onDismiss={handleDeclineErrorDismiss} />}
                <Box sx={styles.nonStickyDeclineContainer}>
                  <DeclineButton jobKey={jobKey} onError={handleDeclineError} />
                </Box>
              </>
            )}
            {!!interviewNowModel && (
              <Box sx={styles.applyButtonNonSticky}>
                <OGWrappedIndeedApplyButton
                  isSecondaryApplyButton={true}
                  hasLoggedImpression={hasLoggedIndeedApplySecondaryCTAImpression}
                  hasLoggedSeen={hasLoggedIndeedApplySeen}
                  setHasLoggedSeen={setHasLoggedIndeedApplySeen}
                />
              </Box>
            )}
            <BottomSaveJobButton />
            <ReportContentButton />
            {isJobAlertSignupEnabled && <ViewJobJobAlertSignup />}
          </WithSafeAreaInsetsTopMargin>
        </ScreenDisplay.MobileOnly>

        <InterviewPrepCTA />
        {hasAdditionalLinksModel && <AdditionalLinks />}
        <FAQFootprint />

        <ScreenDisplay.DesktopOnly>
          {hasAdditionalLinksModel && <ViewJobDivider />}

          <OGWrappedNexusJobDisclaimer />

          <Box sx={[styles.desktopBottomActionRow]}>
            <ReportContentButton />
          </Box>
          {!hasAdditionalLinksModel && <ViewJobDivider />}
          {!hasAdditionalLinksModel && <RelatedLinks />}
        </ScreenDisplay.DesktopOnly>
      </WidthTracker>
    </Box>
  );
}

export default ViewJobMainContent;

export function OGWrappedViewJobMainContent({ reload }: { reload: () => void }) {
  const ogModel = useViewjobOneGraphModel();
  const isFullScreenVJActive = useFullScreenVJActive();

  if (ogModel == null) {
    return null;
  }

  return (
    <ViewJobMainContent
      jobKey={ogModel.job.jobKey}
      loggedIn={ogModel.jobSeeker.loggedIn}
      disclaimer={ogModel.job.viewJobDisclaimer}
      isIndeedApplyJob={!!ogModel.indeedApplyButtonContainer}
      reload={reload}
      variant={isFullScreenVJActive ? ViewJobVariant.FullScreen : ViewJobVariant.Default}
    />
  );
}

const CONTENT_MARGIN = 16;

const styles = StyleSheet.create({
  container: {
    marginBottom: 8,
    position: "relative",
    backgroundColor: "white",
  },
  desktopStandaloneContainer: {
    alignSelf: "center",
    maxWidth: 900,
    width: "100%",
  },
  jobHeader: {
    paddingTop: 8,
  },
  fullScreenVJContainer: {
    paddingTop: 0,
  },
  contentSpacing: {
    paddingStart: CONTENT_MARGIN,
    paddingEnd: CONTENT_MARGIN,
    marginBottom: 16,
  },
  fullScreenVJContentSpacing: {
    marginTop: 16,
    marginBottom: 24,
  },
  contentSpacingWithTabMenu: {
    marginStart: CONTENT_MARGIN,
    marginEnd: CONTENT_MARGIN,
    marginBottom: 16,
    marginTop: 16,
  },
  desktopStandaloneContentSpacing: {
    marginStart: 0,
    marginEnd: 0,
    paddingStart: 0,
    paddingEnd: 0,
  },
  descriptionContainer: {
    marginTop: 20,
    marginBottom: 16,
  },
  extraButtonContainer: {
    marginStart: 24,
    marginEnd: 24,
    marginBottom: 20,
    rowGap: 8,
  },
  savePromptContainer: {
    marginStart: 24,
    marginEnd: 24,
    marginBottom: 8,
  },
  tabMenuContainer: {
    justifyContent: "flex-start",
    paddingHorizontal: 0,
  },
  buttonRowWithDecline: {
    flexDirection: "row",
    paddingHorizontal: 24,
    gap: 8,
  },
  confirmInterestButtonSticky: {
    flex: 1,
  },
  confirmInterestButtonNonSticky: {
    flex: 1,
    paddingBottom: 4,
    marginStart: CONTENT_MARGIN,
    marginEnd: CONTENT_MARGIN,
  },
  applyButtonSticky: {
    flex: 1,
    marginStart: CONTENT_MARGIN,
    marginEnd: CONTENT_MARGIN,
  },
  applyButtonNonSticky: {
    marginTop: 2,
    paddingBottom: Platform.OS === "web" ? 0 : 8,
    marginStart: CONTENT_MARGIN,
    marginEnd: CONTENT_MARGIN,
  },
  nonStickyDeclineContainer: {
    marginBottom: 8,
    marginStart: CONTENT_MARGIN,
    marginEnd: CONTENT_MARGIN,
  },
  buttonRowWithAsk: {
    flexDirection: "row",
    gap: spacing.s2,
    alignItems: "center",
    marginTop: 2,
    paddingBottom: Platform.OS === "web" ? 0 : spacing.s2,
    marginStart: CONTENT_MARGIN,
    marginEnd: CONTENT_MARGIN,
  },
  applyButtonInRow: {
    flex: 1,
    minWidth: 0,
  },
  askCTASpacing: {
    marginTop: spacing.s2,
  },
  desktopJobHeader: {
    position: "sticky" as "absolute",
    top: 0,
    backgroundColor: colors.background,
    overflowAnchor: "none",
    zIndex: 3,
    paddingTop: 24,
    transform: [{ translateY: 0 }],
  } as WebTransitionStyle,
  desktopBrandedJobHeader: {
    paddingTop: 0,
  },
  desktopHeaderState: {
    display: "grid" as "flex",
    gridTemplateRows: "1fr",
    opacity: 1,
    transform: [{ translateY: 0 }],
    transitionDuration: "200ms",
    transitionProperty: "grid-template-rows, opacity, transform",
    transitionTimingFunction: "ease",
    visibility: "visible",
  } as WebTransitionStyle,
  desktopHeaderStateContent: {
    minHeight: 0,
    overflow: "hidden",
  },
  desktopHeaderStateHidden: {
    gridTemplateRows: "0fr",
    opacity: 0,
    transform: [{ translateY: -4 }],
    visibility: "hidden",
  } as WebTransitionStyle,
  desktopHeaderStateReducedMotion: {
    transitionDuration: "0ms",
  } as WebTransitionStyle,
  compactDesktopJobHeader: {
    boxShadow: shadows.low,
    marginBottom: 0,
    paddingTop: 16,
    transform: [{ translateY: -1 }],
  },
  desktopHeaderReducedMotion: {
    transitionDuration: "0ms",
  } as WebTransitionStyle,
  desktopHeaderSentinel: {
    height: 1,
    marginBottom: -1,
  },
  desktopHeaderTransition: {
    transitionDuration: "200ms",
    transitionProperty: "box-shadow, opacity, padding-top, transform",
    transitionTimingFunction: "ease",
  } as WebTransitionStyle,
  desktopBottomActionRow: {
    paddingTop: 12,
    paddingBottom: 12,
    display: "flex",
    flexDirection: "row",
  },
});
